import { assertNotEmpty, type Compare, type Optional } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'
import { Icon, type IconName } from '@festina-life/ui/structure'
import cx from 'classnames'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  INHERITANCE_DIFF_TYPE,
  PREVIOUS_DIFF_TYPE,
} from '../../scenes/template/TemplateNavigationControls'
import type { ComparableField, ComparableValue } from '../../types/diffing'
import { isInherited } from '../../uiModels/templateOperations/types'
import { isSpecialCasedSubTemplatedValueSetup } from '../specialCasedSubTemplatedViewers/types'
import type { CompareFilter } from './valueSetupCompareContext'
import { ValueSetupComparisonSelector } from './ValueSetupComparisonSelector'

export type SerialiseValueParams<F extends ComparableField> = {
  value: Optional<F>
  valueType: 'current' | 'inherited' | 'previous'
}

type Props<F extends ComparableField> = {
  comparableValues: ComparableValue<F>
  serialiseValueFn: (value: SerialiseValueParams<F>) => string
  shouldHideInheritedDiff?: boolean
  compareFilter?: CompareFilter
}

export const ValueSetupDiffIndicators = <F extends ComparableField>({
  comparableValues,
  serialiseValueFn,
  shouldHideInheritedDiff,
  compareFilter,
}: Props<F>) => {
  const {
    templateVersion,
    previousTemplateVersion,
    isDiffingAgainstShadowedVersion,
  } = useTemplateContext()
  assertNotEmpty(templateVersion)

  const serialisedCurrentValue = useMemo(
    () =>
      serialiseValueFn({
        value: comparableValues.current,
        valueType: 'current',
      }) || '',
    [comparableValues, serialiseValueFn],
  )
  const serialisedPreviousValue = useMemo(
    () =>
      serialiseValueFn({
        value: comparableValues.previous,
        valueType: 'previous',
      }),
    [comparableValues.previous, serialiseValueFn],
  )
  const serialisedInheritedValue = useMemo(
    () =>
      serialiseValueFn({
        value: comparableValues.inherited,
        valueType: 'inherited',
      }),
    [comparableValues.inherited, serialiseValueFn],
  )

  const compareValuePrevious = useMemo(
    () =>
      (comparableValues.previous
        ? {
            compare: comparableValues.previous,
            current: comparableValues.current,
          }
        : {
            compare: undefined,
            current: comparableValues.current,
          }) as Compare<F>,
    [comparableValues],
  )

  const compareValueInherited = useMemo(() => {
    if (comparableValues.current) {
      return (
        comparableValues.inherited
          ? {
              compare: comparableValues.inherited,
              current: comparableValues.current,
            }
          : {
              compare: undefined,
              current: comparableValues.current,
            }
      ) as Compare<F>
    }

    return null
  }, [comparableValues])

  const currentValueFieldOrigin = isSpecialCasedSubTemplatedValueSetup(
    comparableValues.current,
  )
    ? comparableValues.current.field.origin
    : comparableValues.current?.origin

  const isEmptyValueForVersion = !currentValueFieldOrigin

  const shouldDiff =
    isEmptyValueForVersion ||
    (comparableValues.current &&
      !isInherited(
        isSpecialCasedSubTemplatedValueSetup(comparableValues.current)
          ? comparableValues.current.field
          : comparableValues.current,
      ))

  if (!shouldDiff) {
    return null
  }

  const isDifferentFromParent =
    !!comparableValues.inherited &&
    serialisedCurrentValue &&
    serialisedInheritedValue &&
    serialisedCurrentValue !== serialisedInheritedValue

  const isDifferentFromPrevious =
    !!previousTemplateVersion &&
    serialisedCurrentValue !== serialisedPreviousValue

  const inheritanceDiffIconName: IconName =
    !!compareValueInherited && !serialisedCurrentValue
      ? 'BarsArrowUp'
      : 'ChevronDoubleUp'

  return (
    <div className="right-2 top-2 flex h-full flex-row items-center gap-x-2">
      <div className={isDifferentFromPrevious ? 'block' : 'invisible'}>
        {!!compareValuePrevious && (
          <Tooltip
            isInteractive
            tip={
              <ValueSetupComparisonSelector
                compareType="previous"
                compareFilter={compareFilter}
                {...compareValuePrevious}
              />
            }
          >
            <Icon
              data-type={PREVIOUS_DIFF_TYPE}
              name="ChevronDoubleLeft"
              className={cx('size-4', {
                'text-diff-previous dark:text-diff-previousDark':
                  !isDiffingAgainstShadowedVersion,
                'text-warning-500 dark:text-warning-300 shadowDiff':
                  isDiffingAgainstShadowedVersion && isDifferentFromPrevious,
              })}
            />
          </Tooltip>
        )}
      </div>
      {!shouldHideInheritedDiff && (
        <div className={isDifferentFromParent ? 'block' : 'invisible'}>
          {!!compareValueInherited && (
            <Tooltip
              isInteractive
              tip={
                <ValueSetupComparisonSelector
                  compareType="inherited"
                  compareFilter={compareFilter}
                  {...compareValueInherited}
                />
              }
            >
              <Icon
                data-type={INHERITANCE_DIFF_TYPE}
                name={inheritanceDiffIconName}
                className={cx('size-4', {
                  'text-diff-parent dark:text-diff-parentDark':
                    !isEmptyValueForVersion,
                  'text-diff-parent/40 dark:text-diff-parentDark/40':
                    isEmptyValueForVersion,
                })}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  )
}
